import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../lib/language';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowLeft, Camera, Dog } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SportOrderPage() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const isSuccess = window.location.pathname.includes('/success');

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`${API}/sport/shop/orders/${orderId}`);
        setOrder(res.data);
      } catch (err) {
        console.error('Failed to load order:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const t = {
    pl: {
      orderSuccess: 'Zamowienie zlozone',
      orderCancelled: 'Zamowienie anulowane',
      successMsg: 'Dziekujemy za zakup! Otrzymasz email z potwierdzeniem.',
      cancelMsg: 'Platnosc zostala anulowana. Mozesz sprobowac ponownie.',
      orderNumber: 'Numer zamowienia',
      total: 'Suma',
      status: 'Status',
      photos: 'zdjec',
      backToEvents: 'Powrot do zawodow',
      paid: 'Oplacone',
      pending: 'Oczekujace',
    },
    en: {
      orderSuccess: 'Order Placed',
      orderCancelled: 'Order Cancelled',
      successMsg: 'Thank you for your purchase! You will receive a confirmation email.',
      cancelMsg: 'Payment was cancelled. You can try again.',
      orderNumber: 'Order number',
      total: 'Total',
      status: 'Status',
      photos: 'photos',
      backToEvents: 'Back to events',
      paid: 'Paid',
      pending: 'Pending',
    },
  }[language] || {};

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/sport" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Dog className="w-6 h-6" />
          <h1 className="text-lg font-bold">Focus Sport</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          {isSuccess ? (
            <>
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h2 className="text-2xl font-bold mb-2">{t.orderSuccess}</h2>
              <p className="text-muted-foreground">{t.successMsg}</p>
            </>
          ) : (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
              <h2 className="text-2xl font-bold mb-2">{t.orderCancelled}</h2>
              <p className="text-muted-foreground">{t.cancelMsg}</p>
            </>
          )}
        </div>

        {order && (
          <Card className="mb-8">
            <CardContent className="p-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t.orderNumber}</span>
                <span className="font-mono">{order.id?.slice(0, 8)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t.total}</span>
                <span className="font-semibold">{order.total_amount} {order.currency}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t.status}</span>
                <span className={`font-medium ${order.status === 'paid' ? 'text-green-500' : 'text-yellow-500'}`}>
                  {order.status === 'paid' ? t.paid : t.pending}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t.photos}</span>
                <span>{order.items?.length || 0} {t.photos}</span>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-center">
          <Link to="/sport">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t.backToEvents}
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
